/**
 * Meals hook
 * TanStack Query hooks for meal data fetching and management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getMeals,
  getMealById,
  getAllTags,
  createMeal,
  updateMeal,
  deleteMeal,
  addTagToMeal,
  removeTagFromMeal,
} from '@/lib/api/meals';
import type { ListMealsQuery, UpdateMealRequest, AddTagRequest } from '@/types/api';
import { mealKeys } from '@/lib/query-keys';

// Re-export for backwards compatibility
export { mealKeys };

/**
 * Hook to fetch paginated and filtered meals
 *
 * Features:
 * - Search by meal name (debounced in component)
 * - Filter by tags
 * - Pagination support
 * - Automatic refetching with staleTime
 *
 * @example
 * ```tsx
 * function MealList() {
 *   const { data, isLoading, error } = useMeals({
 *     query: 'pasta',
 *     tags: ['italian', 'vegetarian'],
 *     page: 1,
 *     limit: 20,
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *
 *   return <div>{data.meals.map(meal => <MealCard key={meal.id} meal={meal} />)}</div>;
 * }
 * ```
 */
export function useMeals(
  params?: ListMealsQuery,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: mealKeys.list(params),
    queryFn: () => getMeals(params),
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to fetch a single meal by ID
 *
 * @example
 * ```tsx
 * function MealDetail({ mealId }: { mealId: number }) {
 *   const { data: meal, isLoading, error } = useMeal(mealId);
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *
 *   return <div>{meal.name}</div>;
 * }
 * ```
 */
export function useMeal(id: number) {
  return useQuery({
    queryKey: mealKeys.detail(id),
    queryFn: () => getMealById(id),
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to fetch all available meal tags with counts
 *
 * Features:
 * - Returns all unique tags across all meals
 * - Includes count of meals per tag
 * - Long cache time (tags change infrequently)
 *
 * @example
 * ```tsx
 * function MealTagFilter() {
 *   const { data, isLoading } = useMealTags();
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <select>
 *       {data.tags.map(tag => (
 *         <option key={tag.tag} value={tag.tag}>
 *           {tag.tag} ({tag.count})
 *         </option>
 *       ))}
 *     </select>
 *   );
 * }
 * ```
 */
export function useMealTags() {
  return useQuery({
    queryKey: mealKeys.tags(),
    queryFn: getAllTags,
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Hook to create a new meal
 *
 * Features:
 * - Creates meal with name and optional tags
 * - Invalidates meal list queries on success
 * - Returns created meal data
 *
 * @example
 * ```tsx
 * function CreateMealForm() {
 *   const createMealMutation = useCreateMeal();
 *
 *   const handleSubmit = async (data: CreateMealRequest) => {
 *     await createMealMutation.mutateAsync(data);
 *   };
 *
 *   return <form onSubmit={handleSubmit}>...</form>;
 * }
 * ```
 */
export function useCreateMeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMeal,
    onSuccess: (newMeal) => {
      // Invalidate meal lists to refetch with new data
      queryClient.invalidateQueries({
        queryKey: mealKeys.lists(),
      });

      // Invalidate tags as new tags may have been added
      queryClient.invalidateQueries({
        queryKey: mealKeys.tags(),
      });

      // Set the new meal in cache
      queryClient.setQueryData(mealKeys.detail(newMeal.id), newMeal);
    },
  });
}

/**
 * Hook to update an existing meal
 *
 * Features:
 * - Updates meal name
 * - Invalidates related queries on success
 * - Returns updated meal data
 *
 * @example
 * ```tsx
 * function EditMealForm({ mealId }: { mealId: number }) {
 *   const updateMealMutation = useUpdateMeal();
 *
 *   const handleSubmit = async (data: UpdateMealRequest) => {
 *     await updateMealMutation.mutateAsync({ id: mealId, data });
 *   };
 *
 *   return <form onSubmit={handleSubmit}>...</form>;
 * }
 * ```
 */
export function useUpdateMeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMealRequest }) => updateMeal(id, data),
    onSuccess: (updatedMeal) => {
      // Update the meal detail in cache
      queryClient.setQueryData(mealKeys.detail(updatedMeal.id), updatedMeal);

      // Invalidate meal lists to reflect updated data
      queryClient.invalidateQueries({
        queryKey: mealKeys.lists(),
      });
    },
  });
}

/**
 * Hook to delete a meal
 *
 * Features:
 * - Deletes meal by ID
 * - Invalidates all meal queries on success
 * - Removes meal from cache
 *
 * @example
 * ```tsx
 * function DeleteMealButton({ mealId }: { mealId: number }) {
 *   const deleteMealMutation = useDeleteMeal();
 *   const navigate = useNavigate();
 *
 *   const handleDelete = async () => {
 *     await deleteMealMutation.mutateAsync(mealId);
 *     navigate({ to: '/meals' });
 *   };
 *
 *   return <button onClick={handleDelete}>Delete</button>;
 * }
 * ```
 */
export function useDeleteMeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteMeal,
    onSuccess: (_, deletedMealId) => {
      // Remove the meal from cache
      queryClient.removeQueries({
        queryKey: mealKeys.detail(deletedMealId),
      });

      // Invalidate meal lists and tags
      queryClient.invalidateQueries({
        queryKey: mealKeys.lists(),
      });

      queryClient.invalidateQueries({
        queryKey: mealKeys.tags(),
      });
    },
  });
}

/**
 * Hook to add a tag to a meal
 *
 * Features:
 * - Adds new tag to existing meal
 * - Supports hidden tags
 * - Updates meal in cache
 * - Invalidates tag list
 *
 * @example
 * ```tsx
 * function AddTagButton({ mealId }: { mealId: number }) {
 *   const addTagMutation = useAddMealTag();
 *
 *   const handleAddTag = async (tag: string, isHidden: boolean) => {
 *     await addTagMutation.mutateAsync({ mealId, data: { tag, isHidden } });
 *   };
 *
 *   return <button onClick={() => handleAddTag('italian', false)}>Add Tag</button>;
 * }
 * ```
 */
export function useAddMealTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mealId, data }: { mealId: number; data: AddTagRequest }) =>
      addTagToMeal(mealId, data),
    onSuccess: (updatedMeal) => {
      // Update the meal detail in cache
      queryClient.setQueryData(mealKeys.detail(updatedMeal.id), updatedMeal);

      // Invalidate meal lists to reflect updated tags
      queryClient.invalidateQueries({
        queryKey: mealKeys.lists(),
      });

      // Invalidate tags as new tag may have been added
      queryClient.invalidateQueries({
        queryKey: mealKeys.tags(),
      });
    },
  });
}

/**
 * Hook to remove a tag from a meal
 *
 * Features:
 * - Removes tag from meal by tag ID
 * - Refetches meal detail after removal
 * - Updates tag list
 *
 * @example
 * ```tsx
 * function RemoveTagButton({ mealId, tagId }: { mealId: number; tagId: number }) {
 *   const removeTagMutation = useRemoveMealTag();
 *
 *   const handleRemove = async () => {
 *     await removeTagMutation.mutateAsync({ mealId, tagId });
 *   };
 *
 *   return <button onClick={handleRemove}>Remove</button>;
 * }
 * ```
 */
export function useRemoveMealTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mealId, tagId }: { mealId: number; tagId: number }) =>
      removeTagFromMeal(mealId, tagId),
    onSuccess: (_, { mealId }) => {
      // Refetch the meal detail to get updated tag list
      queryClient.invalidateQueries({
        queryKey: mealKeys.detail(mealId),
      });

      // Invalidate meal lists to reflect updated tags
      queryClient.invalidateQueries({
        queryKey: mealKeys.lists(),
      });

      // Invalidate tags as tag counts may have changed
      queryClient.invalidateQueries({
        queryKey: mealKeys.tags(),
      });
    },
  });
}
